import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { formsActions } from '../forms-actions';
import { asyncScheduler, catchError, exhaustMap, filter, finalize, map, of, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  selectAiFormFieldsByCollectionFieldsId,
  selectAiFormFieldsByViewDataSourceId,
  selectForm,
  selectFormField
} from '../forms-selectors';
import { CollectionFormField } from '../../../../../models/ts/collection-form-field.model';
import { AiApiService } from '../../../../api/bizzmine/ai/ai-api.service';
import { CollectionFormLinkedService } from '../../../../features/bizzmine/form/services/collection-form-linked.service';
import { CollectionFormValidatorError } from '../../../../features/bizzmine/form/classes/collection-form-validator-error';
import { CollectionFormValidatorResult } from '../../../../features/bizzmine/form/classes/collection-form-validator-result';
import { LinkedCollectionType } from '../../../../../models/ts/linked-collection-type.model';
import {
  CollectionListDataInstance,
} from 'src/app/shared/interfaces/collection-list-data-instance';
import { CollectionFormService } from 'src/app/features/bizzmine/form/services/collection-form.service';
import { LookupItem } from 'src/models/ts/collection-list-summary-item.model';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class FormsAiEffects {
  //Injectables
  private actions$ = inject(Actions);
  private store$ = inject(Store);
  private aiApiService = inject(AiApiService);
  private collectionFormLinkedService = inject(CollectionFormLinkedService);

  //Effects
  public updateFieldWithAi$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.updateFieldWithAi),
    concatLatestFrom(props => [
      this.store$.select(selectForm(props.formId)),
      this.store$.select(selectFormField(props.formId, props.fieldId)),
    ]),
    tap(([props, form, field]) => {
      if (field != undefined && form != undefined) {
        // Set AI properties on field
        this.store$.dispatch(formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: props.formId,
          fieldId: props.fieldId,
          value: { aiValue: props.value, generated: true, confirmed: false},
          isFirstTimeValidation: props.isFirstTimeValidation
        }));

        // Update value / instance based on type
        if (field.IsLookupField) {
          // Lookup
          this.store$.dispatch(formsActions.lookupChanged({
            formId: props.formId,
            lookupFieldId: props.fieldId,
            lookupItem: props.value as LookupItem
          }));
        } else if (CollectionFormService.fieldIsGrid(field)) {
          // Grid
          // Clear existing
          this.collectionFormLinkedService.clearViewDataSource(props.formId, structuredClone(form.data), field.ViewDataSourcesID);
          // Add new instances
          this.store$.dispatch(formsActions.addInstancesToGrid({
            formId: props.formId,
            gridFieldId: props.fieldId,
            instances: props.value as CollectionListDataInstance[],
            aiGenerated: true,
          }));
        } else {
          // Field
          this.store$.dispatch(formsActions.updateFormFieldValue({
            formId: props.formId,
            fieldId: props.fieldId,
            value: props.value as unknown
          }));
        }
      }
    })
  ), ({ dispatch: false }));
  public getAiButtonInGrid$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.getAiButtonDataInGrid),
      map(({ formId, fieldId, gridFieldId, recordId, linkedFields, field }) => {
        //Clear previous form validation errors
        this.store$.dispatch(formsActions.clearFormValidation({
          formId: formId
        }))

        this.store$.dispatch(formsActions.updateAiLoadingInGrid({
          formId: formId,
          fieldId: fieldId,
          gridFieldId: gridFieldId,
          recordId: recordId,
          aiLoading: true
        }));

        let aiSourceFields: CollectionFormField[] | undefined = [];
        const newFields: Array<object> = [];

        if (linkedFields) {
          const tempField = linkedFields.find(obj => obj.CollectionFieldsID == field.AiSourceFields[0].CollectionFieldsID);
          if (tempField != undefined) {
            aiSourceFields.push(tempField);
          }
        } else {
          field?.AiSourceFields.forEach(field => {
            if (LinkedCollectionType.SingleRecord || LinkedCollectionType.None) {
              aiSourceFields.push(structuredClone(this.store$.selectSignal(selectAiFormFieldsByCollectionFieldsId(
                formId, field.CollectionFieldsID
              ))())[0]);
            } else if (LinkedCollectionType.GridRecord) {
              const linkedGrid = structuredClone(this.store$.selectSignal(selectAiFormFieldsByViewDataSourceId(
                formId, field.ViewDataSourcesID
              ))());

              if (linkedGrid[0] && linkedGrid[0].Records) {
                newFields.push({ [field.LinkedCollectionName]: linkedGrid[0].Records });
              }
            }
          });
        }

        const form = this.store$.selectSignal(selectForm(formId))();
        return formsActions.fetchAiButtonData({
          formId: formId,
          field: field,
          gridFieldId: gridFieldId,
          recordId: recordId,
          data: {
            AiLicense: false,
            AiLoading: false,
            FormId: form!.data.CollectionFormId,
            CollectionsId: form!.data.CollectionsID,
            InstancesId: form!.data.InstancesID,
            VersionsId: form!.data.VersionsID,
            AiButtonId: field.CollectionFieldsID ?? 0,
            Fields: aiSourceFields.length > 0 ? aiSourceFields.map(field => ({ [field.Bookmark]: field.Value })) : newFields
          }
        });
      })
    ));
  public getAiButton$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.getAiButtonData),
      map(({ formId, field, linkedFields }) => {
        //Clear existing validation errors
        this.store$.dispatch(formsActions.clearFormValidation({
          formId: formId
        }))

        //Set all ai fields to loading
        this.store$.dispatch(formsActions.updateAiLoading({
          formId: formId,
          fieldId: field?.Id ?? 0,
          aiLoading: true
        }));

        let aiSourceFields: CollectionFormField[] | undefined = [];
        const newFields: Array<object> = [];

        if (linkedFields) {
          const tempField = linkedFields.find(obj => obj.CollectionFieldsID == field.AiSourceFields[0].CollectionFieldsID);
          if (tempField != undefined) {
            aiSourceFields.push(tempField);
          }
        } else {
          field.AiSourceFields.forEach(field => {
            if (LinkedCollectionType.SingleRecord || LinkedCollectionType.None) {
              aiSourceFields.push(structuredClone(this.store$.selectSignal(selectAiFormFieldsByCollectionFieldsId(
                formId, field.CollectionFieldsID
              ))())[0]);
            } else if (LinkedCollectionType.GridRecord) {
              const linkedGrid = structuredClone(this.store$.selectSignal(selectAiFormFieldsByViewDataSourceId(
                formId, field.ViewDataSourcesID
              ))());

              if (linkedGrid[0] && linkedGrid[0].Records) {
                newFields.push({ [field.LinkedCollectionName]: linkedGrid[0].Records });
              }
            }
          });
        }
        const form = this.store$.selectSignal(selectForm(formId))();
        return formsActions.fetchAiButtonData({
          formId: formId,
          field: field,
          gridFieldId: undefined,
          recordId: undefined,
          data: {
            AiLicense: false,
            AiLoading: false,
            FormId: form!.data.CollectionFormId,
            CollectionsId: form!.data.CollectionsID,
            InstancesId: form!.data.InstancesID,
            VersionsId: form!.data.VersionsID,
            AiButtonId: field.CollectionFieldsID ?? 0,
            Fields: aiSourceFields.length > 0 ? aiSourceFields.map(field => ({ [field.Bookmark]: field.Value })) : newFields
          }
        });
      })
    ));
  public aiButtonDataSucceeded$ = createEffect(() => this.actions$.pipe(
        ofType(formsActions.fetchAiButtonDataSucceeded),
        tap(props => {
          // Check if validation needs to be shown when file is too large to process
          if (props.data.StatusCode === 'FileTooLarge') {
            const fileError: CollectionFormValidatorResult = new CollectionFormValidatorResult();
            const error: CollectionFormValidatorError = {
              Bookmark: props.field.Bookmark,
              Message: `File is too large for AI generation, please select another file`,
              CollectionFormFieldID: props.field.Id
            };
            fileError.Errors.push(error);
            this.store$.dispatch(formsActions.formValidationFailed({
              formId: props.formId,
              validationErrors: fileError
            }));
          }

          if (props.data.Result.length > 0) {
            // Set ai state for button
            this.store$.dispatch(formsActions.updateFormFieldAiGeneration({
              gridFieldId: props.gridFieldId ?? undefined,
              recordId: props.recordId ?? undefined,
              formId: props.formId,
              fieldId: props.field.Id ?? 0,
              value: { aiValue: 'AiButton', generated: false, confirmed: undefined, aiButtonCollectionFieldId: props.field.CollectionFieldsID },
              isFirstTimeValidation: true
            }));

            //Set form unsaved changes when ai button has returned values.
            this.store$.dispatch(formsActions.setAiUnsavedChanges({
              formId: props.formId,
              unsavedChanges: true
            }));

            // Update all result fields with AI value
            props.data.Result.forEach(result => {
              this.store$.dispatch(formsActions.updateFieldWithAi({
                formId: props.formId,
                fieldId: result.Key,
                value: result.Value,
                isFirstTimeValidation: true
              }));
            });
            asyncScheduler.schedule(() => {
              // Set validation errors for all result fields (no confirmation check)
              const storeForm = this.store$.selectSignal(selectForm(props.formId))();
              if (storeForm) {
                const validationErrors = CollectionFormService.setFirstTimeAiValidation(
                  storeForm,
                  props.formId,
                  props.data.Result,
                  props.field,
                  false
                );
                if (!validationErrors.IsValid) {
                  this.store$.dispatch(formsActions.formValidationFailed({
                    formId: props.formId,
                    validationErrors
                  }));
                }
              }
            }, 0)

          }
        })
      ), { dispatch: false });
  public fetchAiButtonData$ = createEffect(() => this.actions$.pipe(
    ofType(formsActions.fetchAiButtonData),
    filter(({ data }) => data != undefined && data.Fields != undefined && data.Fields.length > 0),
    exhaustMap((props) => {
      return this.aiApiService.autoComplete(props.data).pipe(map(response => {
        return formsActions.fetchAiButtonDataSucceeded({
          formId: props.formId,
          field: props.field,
          data: response,
          gridFieldId: props.gridFieldId ?? undefined,
          recordId: props.recordId ?? undefined
        });
      }), finalize(() => {
        if (props.gridFieldId != undefined && props.recordId != undefined)
          this.store$.dispatch(formsActions.updateAiLoadingInGrid({
            formId: props.formId,
            fieldId: props.field.CollectionFieldsID,
            aiLoading: false,
            gridFieldId: props.gridFieldId,
            recordId: props.recordId,
          }));
        else
          this.store$.dispatch(formsActions.updateAiLoading({
            formId: props.formId,
            fieldId: props.field.Id,
            aiLoading: false
          }));
      }), catchError(error => {
        return of(formsActions.fetchAiButtonDataFailed({
          formId: props.formId,
          field: props.field,
          error: error
        }));
      }));
    })
  ));

  public clearValidationOnAIAcceptance = createEffect(() => this.actions$.pipe(
    ofType(formsActions.updateFormFieldAiGeneration),
    filter(props => !props.isFirstTimeValidation),
    map(props => formsActions.clearFormFieldValidation({
      formId: props.formId,
      fieldId: props.fieldId
    }))
  ));

  /**
   * Updated the StateForm property indicating if any field has unconfirmed AI generated content
   */
  public checkAiConfirmation$ = createEffect(() => this.actions$.pipe(
    ofType(
      formsActions.acceptAllAiGeneration,
      formsActions.updateFieldWithAi,
      formsActions.updateFormFieldAiGeneration
    ),
    filter(props => !('isFirstTimeValidation' in props) || !props.isFirstTimeValidation),
    map(props => formsActions.checkAiConfirmation({
      formId: props.formId
    }))
  ));


  //User changed something on grids, so we need to clear the AI generation state on the field.
  public removeAiGenerationOnFieldViewDataSourceChange$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.updateFormViewDataSource),
      filter(_ => _.aiGenerated !== true),
      concatLatestFrom(action =>
        this.store$.select(selectForm(action.formId))
      ),
      map(([action, form]) => {
        let field: CollectionFormField | undefined;
        if(form && form.data){
          field = CollectionFormService.getField(form.data, f => f.ViewDataSourcesID === action.viewDataSource.ViewDataSourcesID && !f.IsLookupField && f.aiGeneration !== undefined);
        }
        return { action, field };
      }),
      filter(({ action, field }) =>
        field !== undefined
      ),
      map(({ action, field }) =>
        formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: action.formId,
          fieldId: field?.Id ?? 0,
          value: undefined
        })
      )
    )
  );

  //User removed a record from a grid that had AI generated content, so we need to clear the AI generation state on the field.
  public removeAiGenerationOnFieldViewDataSourceRecordsRemoved$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.removeRecordFromGrid),
      concatLatestFrom(action =>
        this.store$.select(selectForm(action.formId))
      ),
      map(([action, form]) => {
        let field: CollectionFormField | undefined;
        if(form && form.data){
          field = CollectionFormService.getField(form.data, f => f.Id === action.gridFieldId && !f.IsLookupField && f.aiGeneration !== undefined);
        }
        return { action, field };
      }),
      filter(({ action, field }) =>
        field !== undefined
      ),
      map(({ action, field }) =>
        formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: action.formId,
          fieldId: field?.Id ?? 0,
          value: undefined
        })
      )
    )
  );

  public setAiGenerationOnFieldViewDataSourceChange$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.updateFormViewDataSource),
      filter(_ => _.aiGenerated == true),
      concatLatestFrom(action =>
        this.store$.select(selectForm(action.formId))
      ),
      map(([action, form]) => {
        let field: CollectionFormField | undefined;
        if(form && form.data){
          field = CollectionFormService.getField(form.data, f => f.ViewDataSourcesID === action.viewDataSource.ViewDataSourcesID && !f.IsLookupField);
        }
        return { action, field, form };
      }),
      filter(({ action, field, form }) =>
        field !== undefined &&
        !form?.aiAllAccepted
      ),
      map(({ action, field }) =>
        // Set AI properties on field
        formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: action?.formId,
          fieldId: field?.Id ?? 0,
          value: { aiValue: field?.Records, generated: true, confirmed: false},
          isFirstTimeValidation: action.aiGenerated
        })
      )
    )
  );

  public removeAiGenerationOnFieldValueChange$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.updateFormFieldValue),
      concatLatestFrom(({ formId, fieldId }) =>
        this.store$.select(selectFormField(formId, fieldId))
      ),
      filter(([action, field]) =>
        field !== undefined &&
        !field.IsLookupField && // Ignore lookup fields
        field.aiGeneration !== undefined &&
        JSON.stringify(field.aiGeneration.aiValue) !== JSON.stringify(action.value)
      ),
      map(([action, field]) =>
        formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: action.formId,
          fieldId: field?.Id ?? 0,
          value: undefined
        })
      )
    )
  );

  public removeAiGenerationOnLookupChanged$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.lookupChanged),
      concatLatestFrom(({ formId, lookupFieldId }) =>
        this.store$.select(selectFormField(formId, lookupFieldId))
      ),
      filter(([action, field]) =>
        field !== undefined &&
        field.IsLookupField &&
        field.aiGeneration !== undefined &&
        JSON.stringify(field.aiGeneration.aiValue) !== JSON.stringify(action.lookupItem)
      ),
      map(([action, field]) =>
        formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: action.formId,
          fieldId: field?.Id ?? 0,
          value: undefined
        })
      )
    )
  );

  public removeAiGenerationOnLookupCleared$ = createEffect(() => this.actions$.pipe(
      ofType(formsActions.lookupCleared),
      concatLatestFrom(action =>
        this.store$.select(selectForm(action.formId))
      ),
      map(([action, form]) => {
        let field: CollectionFormField | undefined;
        if(form && form.data){
          field = CollectionFormService.getField(form.data, f => f.ViewDataSourcesID === action.viewDataSourceId && f.IsLookupField && f.aiGeneration !== undefined);
        }
        return { action, field };
      }),
      map(({ action, field }) =>
        formsActions.updateFormFieldAiGeneration({
          gridFieldId: undefined,
          recordId: undefined,
          formId: action.formId,
          fieldId: field?.Id ?? 0,
          value: undefined
        })
      )
    )
  );
}

export interface AiButtonResult {
  Key: number,
  Value: AiButtonResultValue
}

export interface AiButtonResponse {
  Result: AiButtonResult[],
  StatusCode: string
}

export type AiButtonResultValue = unknown | CollectionListDataInstance[] | LookupItem;

